import type { JSONContent } from '@tiptap/react';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { parseDecimal } from '@/lib/decimal';
import type { DiscountCodeSource, DiscountSettings } from '@/modules/sales-agent/settings/types';

/**
 * The Sales Agent's "Offer a discount" step: Shopify's `discount_offer` tool with its one author-set
 * input, `offer`, fixed on the chip. Compile bakes the chip's `staticInputs` into the tool step's
 * `inputs.static`, so the value is the author's and never the model's.
 */

export const DISCOUNT_OFFER_TOOL = 'discount_offer';
export const DISCOUNT_OFFER_PROVIDER = 'shopify';
export const DISCOUNT_OFFER_LABEL = 'Offer a discount';

/** Exactly one of: a percentage off, money off (both made in Shopify), or one of the brand's own codes. */
export type DiscountOffer = { percent: number } | { amount: number } | { code: string };

export type DiscountOfferKind = 'percent' | 'amount' | 'code';

// No currency sign on money off: the store's currency is not known here.
export const DISCOUNT_OFFER_KINDS: { kind: DiscountOfferKind; label: string }[] = [
  { kind: 'percent', label: '% off' },
  { kind: 'amount', label: 'Amount off' },
  { kind: 'code', label: 'Brand code' }
];

/** The parts of the brand's Discount Limits an offer has to fit. */
export type DiscountOfferLimits = Pick<DiscountSettings, 'source' | 'codes' | 'maxPercent' | 'maxAmount'>;

/** The kinds a brand can offer: its own codes, or percentage and money off made in Shopify. */
export const discountOfferKindsFor = (source: DiscountCodeSource): DiscountOfferKind[] =>
  source === 'existing' ? ['code'] : ['percent', 'amount'];

const isShopifyDiscount = (provider: unknown, tool: unknown): boolean =>
  tool === DISCOUNT_OFFER_TOOL && typeof provider === 'string' && provider.toLowerCase() === DISCOUNT_OFFER_PROVIDER;

/** The brand's mid-flow Shopify discount tool, when its catalog has one. */
export const discountOfferTool = (tools: ToolDefinitionAction[]): ToolDefinitionAction | undefined =>
  tools.find((tool) => !tool.postProcessing && isShopifyDiscount(tool.provider, tool.tool));

/** True for the discount tool, which is reached through "Offer a discount" only, never as a bare tool. */
export const isDiscountOfferTool = (tool: ToolDefinitionAction): boolean => isShopifyDiscount(tool.provider, tool.tool);

/**
 * Whether a chip is an "Offer a discount" step: any mid-flow Shopify `discount_offer` chip. One
 * without a valid `offer` (placed before this step existed, by the AI, or in the SOP editor)
 * reads as one with no offer yet, so a save or compile refuses it until a value is set.
 */
export function isDiscountOfferChip(attrs: Record<string, unknown>): boolean {
  return attrs['postProcessing'] !== true && isShopifyDiscount(attrs['provider'], attrs['tool']);
}

/**
 * Why a value cannot be offered, or null when it can. With the brand's limits, a percentage or
 * amount above its caps is refused too.
 */
export function discountOfferProblem(
  kind: DiscountOfferKind,
  value: string,
  limits?: Pick<DiscountOfferLimits, 'maxPercent' | 'maxAmount'> | null
): string | null {
  if (kind === 'code') return value.trim() ? null : 'Pick one of the brand’s codes.';
  const number = parseDecimal(value);
  if (kind === 'percent') {
    const max = Math.min(100, limits?.maxPercent ?? 100);
    return number >= 1 && number <= max ? null : `Enter a percentage from 1 to ${max}.`;
  }
  const cap = limits?.maxAmount ?? null;
  if (cap === null) return number > 0 ? null : 'Enter an amount above 0.';
  return number > 0 && number <= cap ? null : `Enter an amount above 0 and up to ${cap}.`;
}

/** The offer a kind and its typed value make, or null while the value is not valid. */
export function toDiscountOffer(kind: DiscountOfferKind, value: string): DiscountOffer | null {
  if (discountOfferProblem(kind, value)) return null;
  if (kind === 'code') return { code: value.trim() };
  return kind === 'percent' ? { percent: Number(value) } : { amount: Number(value) };
}

/** Reads a stored `offer` back, or null when it is unset or not a valid offer. */
export function readDiscountOffer(value: unknown): DiscountOffer | null {
  if (typeof value !== 'object' || value === null) return null;
  const offer = value as Record<string, unknown>;
  if (typeof offer['code'] === 'string') return toDiscountOffer('code', offer['code']);
  if (typeof offer['percent'] === 'number') return toDiscountOffer('percent', String(offer['percent']));
  if (typeof offer['amount'] === 'number') return toDiscountOffer('amount', String(offer['amount']));
  return null;
}

export const discountOfferKind = (offer: DiscountOffer): DiscountOfferKind =>
  'code' in offer ? 'code' : 'percent' in offer ? 'percent' : 'amount';

/** The offer as the chip shows it: "10% off", "5 off", "CHAT10". */
export function discountOfferText(offer: DiscountOffer): string {
  if ('code' in offer) return offer.code;
  return 'percent' in offer ? `${offer.percent}% off` : `${offer.amount} off`;
}

/**
 * Why a stored offer no longer fits the brand's Discount Limits (its code source changed, its code
 * was taken off the list, or it is above a cap), or null when it still does.
 */
export function discountOfferMismatch(offer: DiscountOffer, limits: DiscountOfferLimits): string | null {
  if (!discountOfferKindsFor(limits.source).includes(discountOfferKind(offer))) {
    return limits.source === 'existing'
      ? 'the brand now offers only its own codes, so pick one'
      : 'the brand now generates its codes, so pick a percentage or amount off';
  }
  if ('code' in offer) {
    const listed = limits.codes.some((code) => code.toLowerCase() === offer.code.toLowerCase());
    return listed ? null : `${offer.code} is no longer one of the brand’s codes`;
  }
  if ('percent' in offer) {
    return limits.maxPercent !== null && offer.percent > limits.maxPercent
      ? `${offer.percent}% is above the brand’s ${limits.maxPercent}% maximum`
      : null;
  }
  return limits.maxAmount !== null && offer.amount > limits.maxAmount
    ? `${offer.amount} off is above the brand’s upper cap of ${limits.maxAmount}`
    : null;
}

/** "Offer a discount · 10% off"; just "Offer a discount" until a value is set. */
export const discountChipLabel = (offer: DiscountOffer | null): string =>
  offer ? `${DISCOUNT_OFFER_LABEL} · ${discountOfferText(offer)}` : DISCOUNT_OFFER_LABEL;

/**
 * A mid-flow tool chip for the discount tool. The offer starts unset, so the chip reads as
 * incomplete (and a save is refused) until the author fixes its value.
 */
export const discountOfferChipNode = (tool: ToolDefinitionAction, offer: DiscountOffer | null = null): JSONContent => ({
  type: 'actionChip',
  attrs: {
    actionId: tool.id,
    label: discountChipLabel(offer),
    provider: tool.provider,
    color: tool.color,
    tool: tool.tool,
    description: tool.description,
    inputSchema: tool.inputSchema,
    outputSchema: tool.outputSchema,
    riskLevel: tool.riskLevel,
    staticInputs: { offer }
  }
});
